import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { cn } from '@/lib/cn';

/** 종목 시세 & 투자자 동향 카드 — KIS API.
 *  TickerPage 상단에 노출. 6자리 국내 상장주 한정.
 *  투자자 동향은 KIS inquire-investor 가 제공하는 개인/외국인/기관계 3종. */

interface StockQuote {
  price: number | null;
  changeAmount: number | null;
  changePct: number | null;
  open: number | null;
  prevClose: number | null;
  high: number | null;
  low: number | null;
  high1y: number | null;
  low1y: number | null;
  high52w: number | null;
  low52w: number | null;
  volume: number | null;
  tradeValue: number | null;
  status: 'normal' | 'halt' | 'warning';
}

interface InvestorBucket { qty: number; value: number; }
interface InvestorPeriodSummary {
  individual:  InvestorBucket;
  foreign:     InvestorBucket;
  institution: InvestorBucket;
  totalAbsQty: number;
}
interface InvestorTrend {
  date: string | null;
  individual:  InvestorBucket | null;
  foreign:     InvestorBucket | null;
  institution: InvestorBucket | null;
  totalAbsQty: number;
  today:  InvestorPeriodSummary | null;
  days5:  InvestorPeriodSummary | null;
  days20: InvestorPeriodSummary | null;
}

type PeriodKey = 'today' | 'days5' | 'days20';
// 탭 라벨 고정 — 'today' 키는 백엔드 응답 필드명을 따르지만 UI 라벨은 '전일' (매매 데이터가
// 채워진 가장 최근 영업일 = 전일 확정치). 장중 추정치는 사용하지 않음.
const PERIOD_TABS: { key: PeriodKey; label: string }[] = [
  { key: 'today',  label: '전일' },
  { key: 'days5',  label: '5일' },
  { key: 'days20', label: '20일' },
];

interface QuoteResponse {
  quote: StockQuote | null;
  investors: InvestorTrend | null;
}

const fmtNumber = (v: number | null | undefined, frac = 0) =>
  v == null ? '-' : v.toLocaleString('ko-KR', { maximumFractionDigits: frac, minimumFractionDigits: frac });

/** 거래량/거래대금 — 한국식 단위 표기 (만/억/조). */
function fmtKoreanUnit(v: number | null): string {
  if (v == null) return '-';
  const abs = Math.abs(v);
  if (abs >= 1_0000_0000_0000) return `${(v / 1_0000_0000_0000).toFixed(2)}조`;
  if (abs >= 1_0000_0000) return `${(v / 1_0000_0000).toFixed(0)}억`;
  if (abs >= 1_0000) return `${(v / 1_0000).toFixed(0)}만`;
  return `${v.toLocaleString('ko-KR')}`;
}

/** 등락률 색상 — KOR 시장 컨벤션 (상승 빨강 / 하락 파랑) */
function trendClass(v: number | null | undefined): string {
  if (v == null || v === 0) return 'text-[var(--color-text-sub)]';
  return v > 0 ? 'text-[var(--color-rise)]' : 'text-[var(--color-fall)]';
}

/** 슬라이더 — 좌측 '최저 N원' + 우측 '최고 N원' 라벨.
 *  바: 좌측(최저)부터 현재가 위치까지 파랑→빨강 그라데이션, 그 우측은 회색.
 *  현재가 dot 위에 가격 라벨 노출. */
function RangeSlider({
  label,
  low,
  high,
  current,
}: {
  label: string;
  low: number | null;
  high: number | null;
  current: number | null;
}) {
  const valid = low != null && high != null && current != null && high > low;
  const ratio = valid ? Math.max(0, Math.min(1, (current - low) / (high - low))) : 0;
  const leftPct = `${(ratio * 100).toFixed(2)}%`;
  /* 현재가 라벨 정렬 보정 — position 이 우측 경계 근처일 때 라벨이 슬라이더 밖으로 넘치지 않도록
     translateX 를 동적으로 적용. position > 80% → 우측 정렬, < 20% → 좌측 정렬, 그 외 중앙. */
  const labelTransform =
    ratio > 0.8
      ? 'translateX(-100%)'
      : ratio < 0.2
      ? 'translateX(0%)'
      : 'translateX(-50%)';
  return (
    <div className="text-xs">
      {/* 상단 헤더 — 라벨 (좌측 정렬) */}
      <div className="mb-1.5 text-[var(--color-text-sub)]">{label}</div>
      {/* 슬라이더 본체 */}
      <div className="relative">
        <div className="relative h-1.5 overflow-hidden rounded-full bg-[var(--color-border)]">
          {valid && (
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[var(--color-fall)] to-[var(--color-rise)]"
              style={{ width: leftPct }}
            />
          )}
        </div>
        {valid && (
          <>
            {/* 현재가 라벨 — 바 상단 4px 위. position 따라 좌/중/우 정렬 보정 (dot 과 분리) */}
            <div
              className="absolute whitespace-nowrap rounded bg-[var(--color-text)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-primary-inv)] shadow"
              style={{
                left: leftPct,
                bottom: 'calc(100% + 4px)',
                transform: labelTransform,
              }}
            >
              {fmtNumber(current)}원
            </div>
            {/* dot — 바 중앙에 정확히 정렬 (라벨 transform 영향 받지 않도록 분리) */}
            <div
              className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-[var(--color-text)] shadow"
              style={{ left: leftPct }}
            />
          </>
        )}
      </div>
      {/* 하단 — 좌측 '최저 N원' / 우측 '최고 N원' */}
      <div className="mt-1.5 flex items-center justify-between text-[var(--color-text-sub)] tabular-nums">
        <span>{valid ? `최저 ${fmtNumber(low)}원` : '최저 -'}</span>
        <span>{valid ? `최고 ${fmtNumber(high)}원` : '최고 -'}</span>
      </div>
    </div>
  );
}

/** 투자자 동향 — 중앙 기준선 양방향 막대.
 *  양수(순매수): 우측 빨강, 음수(순매도): 좌측 파랑.
 *  막대 길이는 항목 절대값 / 전체 항목 최대 절대값 비율. */
function InvestorBar({
  label,
  qty,
  maxAbs,
}: {
  label: string;
  qty: number | null | undefined;
  maxAbs: number;
}) {
  if (qty == null) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-16 shrink-0 text-sm text-[var(--color-text-sub)]">{label}</div>
        <div className="relative h-3 flex-1">
          <div className="absolute inset-y-0 left-1/2 w-px bg-[var(--color-border)]" />
        </div>
        <div className="w-24 shrink-0 text-right text-sm text-[var(--color-text-sub)]">-</div>
      </div>
    );
  }
  const absQty = Math.abs(qty);
  const ratio = maxAbs > 0 ? Math.min(1, absQty / maxAbs) : 0;
  // 중앙(50%) 기준으로 양수면 우측, 음수면 좌측. 막대 길이는 ratio * 50%.
  const widthPct = `${(ratio * 50).toFixed(1)}%`;
  const isBuy = qty > 0;
  const sign = qty > 0 ? '+' : '';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 shrink-0 text-sm text-[var(--color-text-sub)]">{label}</div>
      <div className="relative h-3 flex-1 rounded-sm bg-[var(--color-bg)]">
        {/* 막대 */}
        <div
          className={cn(
            'absolute inset-y-0 rounded-sm',
            isBuy ? 'left-1/2 bg-[var(--color-rise)]' : 'right-1/2 bg-[var(--color-fall)]',
          )}
          style={{ width: widthPct }}
        />
        {/* 중앙 기준선 — 막대 위에 표시 */}
        <div className="pointer-events-none absolute -inset-y-0.5 left-1/2 w-px bg-[var(--color-text-sub)]" />
      </div>
      <div
        className={cn(
          'w-24 shrink-0 text-right text-sm tabular-nums',
          qty > 0 ? 'text-[var(--color-rise)]' : qty < 0 ? 'text-[var(--color-fall)]' : 'text-[var(--color-text-sub)]',
        )}
      >
        {sign}{fmtNumber(qty)}
      </div>
    </div>
  );
}

export function StockQuoteCard({ ticker }: { ticker: string }) {
  const [period, setPeriod] = useState<PeriodKey>('today');
  const { data, isLoading } = useQuery({
    queryKey: ['stocks', 'quote', ticker],
    queryFn: () => get<QuoteResponse>(`/stocks/${ticker}/quote`),
    enabled: !!ticker && /^\d{6}$/.test(ticker),
    staleTime: 60_000,
    refetchInterval: false,
    retry: 1,
  });

  if (isLoading) {
    return (
      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:p-6">
        <Skeleton className="mb-4 h-8 w-1/3" />
        <Skeleton className="mb-3 h-4 w-full" />
        <Skeleton className="mb-3 h-4 w-full" />
        <Skeleton className="mb-3 h-4 w-full" />
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
        <div className="mt-5 space-y-2">
          <Skeleton className="h-4" />
          <Skeleton className="h-4" />
          <Skeleton className="h-4" />
        </div>
      </section>
    );
  }

  const quote = data?.quote ?? null;
  const investors = data?.investors ?? null;

  if (!quote && !investors) {
    return null;  // 데이터 없음 — 카드 숨김 (AI 분석은 그대로 노출)
  }

  const changePct = quote?.changePct ?? null;
  const changeAmount = quote?.changeAmount ?? null;
  const trend = trendClass(changePct);
  const sign = (changePct ?? 0) > 0 ? '+' : '';

  // 선택된 기간의 누계 — 백엔드가 today/days5/days20 누계를 미리 계산해 보내줌.
  // 누계 필드 미존재(구 응답) 폴백 — investors 직속 individual/foreign/institution 사용.
  const periodSummary: InvestorPeriodSummary | null = investors
    ? investors[period] ??
      (investors.individual && investors.foreign && investors.institution
        ? {
            individual:  investors.individual,
            foreign:     investors.foreign,
            institution: investors.institution,
            totalAbsQty: investors.totalAbsQty,
          }
        : null)
    : null;
  // 막대 길이 정규화용 — 선택된 기간의 절대값 최대
  const investorMaxAbs = periodSummary
    ? Math.max(
        Math.abs(periodSummary.individual.qty),
        Math.abs(periodSummary.foreign.qty),
        Math.abs(periodSummary.institution.qty),
      )
    : 0;

  return (
    <section className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
      {/* ── 시세 영역 ─────────────────────────────────────── */}
      {quote && (
        <div className="p-4 sm:p-6">
          <h2 className="mb-3 section-title">시세</h2>
          {/* 현재가 + 등락 */}
          <div className="flex items-baseline gap-2">
            <span className={cn('text-3xl font-extrabold tabular-nums', trend)}>
              {fmtNumber(quote.price)}원
            </span>
            <span className={cn('text-sm font-semibold tabular-nums', trend)}>
              {sign}{fmtNumber(changeAmount)} ({sign}{changePct != null ? changePct.toFixed(2) : '-'}%)
            </span>
          </div>

          {/* 1일 / 52주 / 1년 슬라이더 — 좌측 '최저 N원' + 우측 '최고 N원' + dot/라벨 */}
          <div className="mt-7 space-y-7">
            <RangeSlider label="1일"  low={quote.low}    high={quote.high}    current={quote.price} />
            <RangeSlider label="52주" low={quote.low52w} high={quote.high52w} current={quote.price} />
            <RangeSlider label="1년"  low={quote.low1y}  high={quote.high1y}  current={quote.price} />
          </div>

          {/* 하단 2x2 그리드 */}
          <dl className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-[var(--color-border)] pt-4 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-[var(--color-text-sub)]">시작가</dt>
              <dd className="font-semibold tabular-nums">{fmtNumber(quote.open)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-[var(--color-text-sub)]">거래량</dt>
              <dd className="font-semibold tabular-nums">{fmtKoreanUnit(quote.volume)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-[var(--color-text-sub)]">종가(전일)</dt>
              <dd className="font-semibold tabular-nums">{fmtNumber(quote.prevClose)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-[var(--color-text-sub)]">거래대금</dt>
              <dd className="font-semibold tabular-nums">{fmtKoreanUnit(quote.tradeValue)}</dd>
            </div>
          </dl>
        </div>
      )}

      {/* ── 투자자 동향 영역 ─────────────────────────────── */}
      {investors && (
        <div className="border-t border-[var(--color-border)] bg-[var(--color-bg)]/40 p-4 sm:p-6">
          <h2 className="section-title mb-3">투자자 동향</h2>
          {/* 기간 탭 — 섹션 헤더 아래 좌측. 활성 = primary 배경 채움 + 볼드 */}
          <div className="mb-2 inline-flex gap-1.5">
            {PERIOD_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setPeriod(t.key)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs transition-colors',
                  period === t.key
                    ? 'bg-[var(--color-primary)] font-bold text-white shadow-sm'
                    : 'bg-[var(--color-bg)] font-medium text-[var(--color-text-sub)] hover:text-[var(--color-text)]',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="mb-3 text-xs text-[var(--color-text-sub)]">
            {investors.date ? `${investors.date} 기준 순매수 수량 (주)` : '순매수 수량 (주)'}
          </div>
          {/* 매도/매수 헤더 — 중앙 기준선과 정렬해 좌/우 의미 명시 */}
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold">
            <div className="w-16 shrink-0" />
            <div className="flex flex-1 items-center">
              <span className="flex-1 text-right text-[var(--color-fall)]">매도</span>
              <span className="mx-2 inline-block h-3 w-px bg-[var(--color-text-sub)]" />
              <span className="flex-1 text-left text-[var(--color-rise)]">매수</span>
            </div>
            <div className="w-24 shrink-0" />
          </div>
          <div className="space-y-3">
            <InvestorBar label="개인"   qty={periodSummary?.individual.qty}  maxAbs={investorMaxAbs} />
            <InvestorBar label="외국인" qty={periodSummary?.foreign.qty}     maxAbs={investorMaxAbs} />
            <InvestorBar label="기관계" qty={periodSummary?.institution.qty} maxAbs={investorMaxAbs} />
          </div>
        </div>
      )}
    </section>
  );
}

